import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import ExcelJS from "exceljs";
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
    Popover,
} from '@mui/material';
import {
    Phone,
    PhoneDisabled,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from "state/axios";
import {
    fetchGroupedLegMembersByCrewCode,
    fetchGroupedRosterDetailsByCrewCode,
} from 'state/api';
import { getFlagUrl, formatTZDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";
import CrewMemberDialog from './CrewMemberDialog';
import RosterDetailsDialog from './RosterDetailsDialog';
import LegMembersDialog from './LegMembersDialog';


const getTime = (flight, field, timeMode) =>
    timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];

const parseName = (rawName) => {
    const clean = (rawName || '').replace(/^\([A-Z]+\)\s*/, '');
    const [first, ...rest] = clean.split(' ');
    return [first || '', rest.join(' ')];
};

const DelayPopover = ({ anchorEl, onClose, description }) => (
    <Popover
        container={document.body}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
        <Box sx={{ fontSize: '12px', padding: 1 }}>
            <Typography>{description}</Typography>
        </Box>
    </Popover>
);

const CrewListDialog = ({
    open,
    onClose,
    title = "CREW LIST",
    flight,
    crewList,
    jumpList,
    isLocked = false,
}) => {
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    // { open, member }
    const [crewMemberDialog, setCrewMemberDialog] = useState({ open: false, member: null });

    // { type: 'roster'|'leg'|null, crewCode, crewName, rosters }
    const [subDialog, setSubDialog] = useState({ type: null, crewCode: '', crewName: '', rosters: [] });

    const [popoverInfo, setPopoverInfo] = useState({ anchorEl: null, description: '' });

    const [reportType, setReportType] = useState('apis');
    const [reportName, setReportName] = useState('all');

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );

    const flagUrl = useMemo(() => {
        const nationalCode = paramsMap.get("AIRLINE_COUNTRY_CODE") || AIRLINE_COUNTRY_CODE || "VN";
        return timeMode === "UTC"
            ? (getFlagUrl("un") || UnitedNationsFlag)
            : (getFlagUrl(nationalCode) || VietnamFlag);
    }, [timeMode, paramsMap]);

    const jumpListFiltered = useMemo(
        () => jumpList?.filter(s => s.seaterType !== 'crew') ?? [],
        [jumpList]
    );

    useEffect(() => {
        if (!flight?.dep && !flight?.arr) return;

        const fetchData = async () => {
            try {
                const res = await api.get('/airports');
                const depCountryCode = res.data?.find(a => a.iataCode === flight.dep)?.countryCode?.toLowerCase() || '';
                const arrCountryCode = res.data?.find(a => a.iataCode === flight.arr)?.countryCode?.toLowerCase() || '';

                const validReports = ['z', 'vm'];
                if (validReports.includes(depCountryCode)) {
                    setReportName(depCountryCode);
                } else if (validReports.includes(arrCountryCode)) {
                    setReportName(arrCountryCode);
                } else {
                    setReportName('all');
                }
            } catch (error) {
                toast.error(error?.message || "Failed to fetch airports!");
            }
        };

        fetchData();
    }, [flight?.dep, flight?.arr]);

    const handleCrewMemberClick = useCallback(async (crewCode) => {
        try {
            const res = await api.get(`/crewmembers/read/${crewCode}`);
            setCrewMemberDialog({
                open: true,
                member: {
                    ...res.data,
                    contactTel: res.data.contactTel || res.data.contactCell || '',
                },
            });
        } catch (error) {
            toast.error(error?.message || "Failed to fetch crew member!");
        }
    }, []);

    const handleCloseCrewMemberDialog = useCallback(() => {
        setCrewMemberDialog({ open: false, member: null });
    }, []);

    const handleRosterDetailsClick = useCallback(async (param) => {
        if (!startDate || !endDate) return;

        const crewCode = param.crewCode || '';
        const crewName = param.crewName || '';

        try {
            const rosters = await fetchGroupedLegMembersByCrewCode(crewCode, startDate, endDate);
            setSubDialog({ type: 'leg', crewCode, crewName, rosters });
            return;
        } catch { /* fall through to roster details */ }

        try {
            const rosters = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
            setSubDialog({ type: 'roster', crewCode, crewName, rosters });
        } catch { /* ignore */ }
    }, [startDate, endDate]);

    const handleCloseSubDialog = useCallback(() => {
        setSubDialog({ type: null, crewCode: '', crewName: '', rosters: [] });
    }, []);

    const handlePopoverOpen = useCallback((event, description) => {
        event.preventDefault();
        event.stopPropagation();
        setPopoverInfo({ anchorEl: event.currentTarget, description });
    }, []);

    const handlePopoverClose = useCallback(() => {
        setPopoverInfo({ anchorEl: null, description: '' });
    }, []);

    const exportCrewList = useCallback(() => {
        const doc = new jsPDF();
        let yOffset = 20;
        const xOffset = 20;
        let pageCount = 0;

        const addPageNumber = (pageNum) => {
            const pageText = `Page ${pageNum}`;
            const xCenter = (doc.internal.pageSize.width - doc.getTextWidth(pageText)) / 2;
            doc.text(pageText, xCenter, doc.internal.pageSize.height - 10);
        };

        const drawLine = (y) => doc.line(xOffset, y, doc.internal.pageSize.width - xOffset, y);

        const checkPage = () => {
            if (yOffset > doc.internal.pageSize.height - 30) {
                addPageNumber(++pageCount);
                doc.addPage();
                yOffset = 20;
            }
        };

        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text(title, 105, yOffset, null, null, 'center');
        yOffset += 15;

        doc.setFontSize(18);
        doc.setFont("helvetica", "normal");
        if (flight) {
            doc.text(
                timeMode === 'UTC'
                    ? `${formatTZDate(flight.flightDate, "EEE dd/MM/yyyy")} | ${flight.flightDesc} ${flight.dep}-${flight.arr}`
                    : `${formatTZDate(flight.flightDateLocalBase, "UTC", "EEE dd/MM/yyyy")} | ${flight.flightDesc} ${flight.dep}-${flight.arr}`,
                20, yOffset
            );
            yOffset += 10;
            doc.text(`AC Type: ${flight.acType} | Tail: ${flight.flightReg}`, 20, yOffset);
            yOffset += 10;
            doc.text(
                `Etd: ${getTime(flight, 'etd', timeMode)} (${flight.etd}Z) | Eta: ${getTime(flight, 'eta', timeMode)} (${flight.eta}Z)`,
                20, yOffset
            );
            yOffset += 10;
        }

        drawLine(yOffset);
        yOffset += 10;

        doc.setFontSize(16);
        crewList?.forEach((crew) => {
            doc.text(
                `${crew.crewName || 'N/A'} | ${crew.crewCode || ''} | ${crew.post || ''} | ${crew.base || ''}${crew.pax ? ` | (${crew.pax})` : ''}${crew.trnDuty ? ` | (${crew.trnDuty})` : ''}`,
                20, yOffset
            );
            yOffset += 10;
            checkPage();
        });

        jumpListFiltered.forEach((staff) => {
            doc.text(`${staff.staffName || 'N/A'} | (JS)`, 20, yOffset);
            yOffset += 10;
            checkPage();
        });

        doc.save(`CrewList_${flight.flightNo}_${formatTZDate(flight.flightDate, "UTC", "ddMMMyyyy")}.pdf`);
    }, [flight, crewList, jumpListFiltered, timeMode, title]);

    const exportFlightInfo = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("FlightInfo");

        worksheet.mergeCells("A1:J1");
        const cellA1 = worksheet.getCell("A1");
        cellA1.value = "FLIGHT INFORMATION";
        cellA1.alignment = { vertical: "middle", horizontal: "center" };
        cellA1.font = { bold: true, size: 14 };

        worksheet.mergeCells("A2:J2");
        const cellA2 = worksheet.getCell("A2");
        cellA2.value = "Times in UTC";
        cellA2.alignment = { vertical: "middle", horizontal: "center" };
        cellA2.font = { italic: true };

        worksheet.addRow();

        const headerRow = worksheet.addRow([
            "DATE", "FLT", "REG", "AC", "DEP", "ARR", "STD", "STA", "JumpSeaters", "Crew",
        ]);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, size: 11 };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD3D3D3" } };
        });

        worksheet.columns = [
            { width: 12 }, { width: 8 }, { width: 10 }, { width: 8 },
            { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 },
            { width: 40 }, { width: 40 },
        ];

        const startRow = 5;
        const maxLength = Math.max(jumpList.length, crewList.length);

        jumpList?.forEach((seater, i) => {
            worksheet.getCell(`I${startRow + i}`).value = seater.staffName;
        });

        crewList?.forEach((crew, i) => {
            worksheet.getCell(`J${startRow + i}`).value = `${crew.crewName} (${crew.post}) ${crew.crewCode}`;
        });

        const endRow = startRow + maxLength - 1;

        const applyCellStyles = (cell) => {
            cell.border = {
                top: { style: "thin" }, left: { style: "thin" },
                bottom: { style: "thin" }, right: { style: "thin" },
            };
            cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
        };

        const flightDetails = [
            formatTZDate(flight.flightDate, "UTC", "dd/MM/yyyy"),
            flight.flightNo, flight.flightReg, flight.acType,
            flight.dep, flight.arr, flight.etd, flight.eta,
        ];

        flightDetails.forEach((value, i) => {
            const col = String.fromCharCode(65 + i);
            const cell = worksheet.getCell(`${col}${startRow}`);
            cell.value = value;
            applyCellStyles(cell);
            worksheet.mergeCells(`${col}${startRow}:${col}${endRow}`);
        });

        worksheet.eachRow(row => row.eachCell(applyCellStyles));

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `FlightInfo_${flight.flightNo}_${formatTZDate(flight.flightDate, "UTC", 'yyyyMMdd')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [flight, crewList, jumpList]);

    const exportAPIS = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("APIS");

        const chinaAPIS = () => {
            worksheet.addRow([
                "*Last name", "*First name", "*Middle name", "*Nationality",
                "*Gender (M/F/U)", "*Date of Birth (YYYYMMDD)", "*Type of crew (FM)",
                "*Type of exit/entry documents (P/T)", "*Exit/entry documents number",
                "Exit/entry documents issuing date YYYYMMDD)",
                "*Expiration date of exit/entry documents YYYYMMDD)",
                "Exit/entry documents issuing country", "Type of visa/entry permit (V)",
                "Visa/entry permit number", "Visa/entry permit issuing date",
                "Visa/entry permit expiration date", "Country of residence",
                "Address in China", "*Initial point of embarkation",
                "*Last point of disembarkation",
            ]);
            worksheet.getRow(1).font = { bold: true };

            const addRow = (d, rawName) => {
                const [firstName, lastName] = parseName(rawName);
                worksheet.addRow([
                    firstName, lastName, '',
                    d?.passportCountry || d?.nationality || '',
                    d?.gender?.charAt(0) || '',
                    d?.birthday ? formatTZDate(d.birthday, "UTC", 'yyyyMMdd') : '',
                    'FM', 'P',
                    d?.passportNumber || '', '',
                    d?.passportExpiryDate ? formatTZDate(d.passportExpiryDate, "UTC", 'yyyyMMdd') : '',
                    d?.passportCountry, '', '', '', '',
                    d?.passportCountry, '',
                    flight.dep || '', flight.arr || '',
                ]);
            };

            crewList.forEach(c => addRow(c.crewDetails, c.crewDetails?.passportName || ''));
            jumpListFiltered.forEach(js => addRow(js.nonCrewDetails, js.nonCrewDetails?.passportName || js.staffName || ''));
        };

        const macauAPIS = () => {
            worksheet.addRow([
                "No.", "*LastName", "*FirstName", "*MiddleName", "*Gender", "*Nationality",
                "*DateOfBirth", "Birthplace", "Residence", "*PersonCategory", "ClearancePort",
                "*BoardingAirport", "*ArrivalAirport", "*DocType", "*DocNo.", "*Expirydate",
                "*IssuePlace", "IssuingOrganization", "*DocType", "*DocNo.", "*ExpiryDate",
                "*IssuePlace", "IssuingOrganization", "SEA", "AVF", "ABO", "TEL", "FX",
                "EM", "BaggageCount", "BaggageWeight", "Unit", "BaggageTag",
                "Country", "State", "City", "PostalCode", "Address",
            ]);
            worksheet.getRow(1).font = { bold: true };

            let rowNo = 0;

            const addRow = (d, rawName) => {
                const [firstName, lastName] = parseName(rawName);
                rowNo += 1;
                worksheet.addRow([
                    rowNo, firstName, lastName, '',
                    d?.gender?.charAt(0) || '',
                    d?.passportCountry || d?.nationality || '',
                    d?.birthday ? formatTZDate(d.birthday, "UTC", 'yyyy-MM-dd') : '',
                    d?.passportCountry, d?.passportCountry,
                    'FM', flight.dep || '', flight.dep || '', flight.arr || '',
                    'P',
                    d?.passportNumber || '',
                    d?.passportExpiryDate ? formatTZDate(d.passportExpiryDate, "UTC", 'yyyy-MM-dd') : '',
                    d?.passportCountry,
                    '', '', '', '', '', '',          // IssuingOrg, DocType2, DocNo2, ExpiryDate2, IssuePlace2, IssuingOrg2
                    '', '', '', '', '', '',          // SEA, AVF, ABO, TEL, FX, EM
                    '', '', '', '',                  // BaggageCount, BaggageWeight, Unit, BaggageTag
                    d?.passportCountry,
                    '', '', '', '',                  // State, City, PostalCode, Address
                ]);
            };

            crewList.forEach(c => addRow(c.crewDetails, c.crewDetails?.passportName || ''));
            jumpListFiltered.forEach(js => addRow(js.nonCrewDetails, js.nonCrewDetails?.passportName || js.staffName || ''));
        };

        const generalAPIS = () => {
            worksheet.addRow([
                "Document Type", "Nationality", "Document Number", "Document Expiry Date",
                "Issuing State", "Family Name", "Given Names", "Date of Birth", "Sex",
                "Country of Birth", "Travel Type", "Override", "Response",
            ]);
            worksheet.getRow(1).font = { bold: true };

            [
                ["***VERSION 4", ""], ["***HEADER", ""], ["***BATCH", "APP"],
                ["*TYPE", "C"], ["*DIRECTION", "I"],
                ["*FLIGHT", `${flight.flightNo}`],
                ["*DEP PORT", flight.dep],
                ["*DEP DATE", formatTZDate(flight.flightActualDayDep, "UTC", 'dd-MMM-yyyy')],
                ["*DEP TIME", flight.etd],
                ["*ARR PORT", flight.arr],
                ["*ARR DATE", formatTZDate(flight.flightActualDayArr, "UTC", 'dd-MMM-yyyy')],
                ["*ARR TIME", flight.eta],
                ["*TB PORT", ""], ["*TB DATE", ""], ["*TB TIME", ""], ["", ""], ["***START", ""],
            ].forEach(r => worksheet.addRow(r));

            const addRow = (d, rawName) => {
                const [firstName, lastName] = parseName(rawName);
                worksheet.addRow([
                    'P',
                    d?.passportCountry || d?.nationality || '',
                    d?.passportNumber || '',
                    d?.passportExpiryDate ? formatTZDate(d.passportExpiryDate, "UTC", 'dd-MMM-yyyy') : '',
                    d?.passportCountry,
                    firstName, lastName,
                    d?.birthday ? formatTZDate(d.birthday, "UTC", 'dd-MMM-yyyy') : '',
                    d?.gender?.charAt(0) || '',
                    d?.passportCountry, 'N', '', '',
                ]);
            };

            crewList.forEach(c => addRow(c.crewDetails, c.crewDetails?.passportName || ''));
            jumpListFiltered.forEach(js => addRow(js.nonCrewDetails, js.nonCrewDetails?.passportName || js.staffName || ''));
            worksheet.addRow(["***END"]);
        };

        switch (reportName) {
            case 'z': chinaAPIS(); break;
            case 'vm': macauAPIS(); break;
            default: generalAPIS(); break;
        }

        worksheet.columns.forEach(col => { col.width = 15; });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `APIS_${flight.flightNo}_${formatTZDate(flight.flightDate, "UTC", 'yyyyMMdd')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [flight, crewList, jumpListFiltered, reportName]);

    const generateExport = useCallback(async () => {
        switch (reportType) {
            case 'apis': await exportAPIS(); break;
            case 'flightInfo': await exportFlightInfo(); break;
            case 'crewList': exportCrewList(); break;
            default:
                toast.error("Invalid report type selected.");
        }
    }, [reportType, exportAPIS, exportFlightInfo, exportCrewList]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '96%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween>
                    <Typography fontWeight="bold" color={primaryDark}>
                        {title}
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{
                                cursor: "pointer",
                                objectFit: "cover",
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>
            </DialogTitle>

            <DialogContent>
                {!flight ? (
                    <Typography>No flights available</Typography>
                ) : (
                    <Box py={0.5} borderBottom={`1px solid ${neutralDark}`}>
                        <Box py={0.5}>
                            <Typography variant="h6">
                                {(() => {
                                    const dateField = timeMode === 'UTC' ? flight?.flightDate : flight?.flightDateLocalBase;
                                    return dateField
                                        ? [
                                            formatTZDate(dateField, "UTC", "EEE dd/MM/yyyy"),
                                            `${flight.flightDesc} ${flight.sector}`,
                                            !isMobile && flight.flightType,
                                        ].filter(Boolean).join(' | ')
                                        : "Invalid Date";
                                })()}
                            </Typography>

                            <Typography variant="h6">
                                {[
                                    flight.acType && `AC Type: ${flight.acType}`,
                                    flight.flightReg && `Tail: ${flight.flightReg}`,
                                    flight.noOfPax && `Pax: ${flight.noOfPax}`,
                                    flight.loadFactor && `LF: ${(flight.loadFactor * 100)?.toFixed(0)}%`,
                                ].filter(Boolean).join(" | ")}
                            </Typography>

                            <FlexBetween>
                                <Typography variant="h6">
                                    Std: {getTime(flight, 'std', timeMode)} ({flight.std}Z)
                                </Typography>
                                <Typography variant="h6">
                                    Sta: {getTime(flight, 'sta', timeMode)} ({flight.sta}Z)
                                </Typography>
                            </FlexBetween>

                            <FlexBetween>
                                <Typography
                                    variant="h6"
                                    sx={{ cursor: flight?.depDelaysTime > 15 ? 'pointer' : 'default' }}
                                    onClick={(e) => {
                                        if (!(flight?.depDelaysTime > 15)) return;
                                        const msg = [
                                            flight.scheduleTat && `TAT: ${flight.scheduleTat}`,
                                            flight.flightDepDelays && `Delay: ${flight.flightDepDelays}`,
                                            flight.remarks,
                                        ].filter(Boolean).join('; ');
                                        handlePopoverOpen(e, msg);
                                    }}
                                >
                                    {(flight.atd || flight.etd) &&
                                        `${flight.atd ? 'Atd' : 'Etd'}: ${getTime(flight, flight.atd ? 'atd' : 'etd', timeMode)} (${flight.atd || flight.etd}Z)`}
                                </Typography>

                                <Typography
                                    variant="h6"
                                    sx={{ cursor: flight?.arrDelaysTime > 15 ? 'pointer' : 'default' }}
                                    onClick={(e) => {
                                        if (!(flight?.arrDelaysTime > 15)) return;
                                        handlePopoverOpen(e, `Delay: ${flight.flightArrDelays ?? ''}`);
                                    }}
                                >
                                    {(flight.ata || flight.eta) &&
                                        `${flight.ata ? 'Ata' : 'Eta'}: ${getTime(flight, flight.ata ? 'ata' : 'eta', timeMode)} (${flight.ata || flight.eta}Z)`}
                                </Typography>
                            </FlexBetween>
                        </Box>

                        {(crewList.length > 0 || jumpListFiltered.length > 0) && <Divider/>}

                        {crewList.length > 0 && crewList.map((crew, idx) => (
                            <FlexBetween key={idx} mt={0.5}>
                                <FlexBetween
                                    gap={0.5}
                                    sx={{
                                        color: crew?.gender === 'FM'
                                            ? theme.palette.secondary.main
                                            : theme.palette.primary.dark,
                                    }}
                                >
                                    <Typography width="18px" textAlign='right'>
                                        {idx + 1}.
                                    </Typography>
                                    <Typography
                                        flex={1}
                                        sx={{
                                            cursor: isLocked ? "default" : 'pointer',
                                            '&:hover': { color: isLocked ? "inherit" : theme.palette.info.light },
                                        }}
                                        onClick={isLocked ? undefined : () => handleCrewMemberClick(crew.crewCode)}
                                    >
                                        {crew.crewName || 'N/A'}
                                    </Typography>
                                </FlexBetween>
                                <FlexBetween gap={0.5}>
                                    <Typography
                                        width={isMobile ? "30px" : "42px"}
                                        textAlign="right"
                                        sx={{
                                            cursor: isLocked ? "default" : 'pointer',
                                            '&:hover': { color: isLocked ? "inherit" : theme.palette.info.light },
                                        }}
                                        onClick={isLocked ? undefined : () => handleRosterDetailsClick(crew)}
                                    >
                                        {crew.crewCode || ''}
                                    </Typography>
                                    <Typography width={isMobile ? "20px" : "40px"} textAlign="center">{crew.post || ''}</Typography>
                                    <Typography width={isMobile ? "25px" : "32px"} textAlign="center">{crew.base || ''}</Typography>
                                    <Typography width="10px" textAlign="center">{crew.pax || ''}</Typography>
                                    <Typography width="10px" textAlign="center">{crew.trnDuty || ''}</Typography>
                                    {crew.contactNo ? (
                                        <Typography
                                            width="16px"
                                            aria-label={`Call ${crew.contactNo}`}
                                            title={`Call ${crew.contactNo}`}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            component="a"
                                            href={`tel:${crew.contactNo.replace(/\D/g, '').trim()}`}
                                            sx={{ textDecoration: 'none', cursor: 'pointer', color: 'yellow.dark' }}
                                        >
                                            <Phone />
                                        </Typography>
                                    ) : (
                                        <Typography
                                            width="16px"
                                            aria-label="No contact number"
                                            title="No contact number"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            sx={{ textDecoration: 'none', cursor: 'not-allowed', color: 'grey' }}
                                        >
                                            <PhoneDisabled />
                                        </Typography>
                                    )}
                                </FlexBetween>
                            </FlexBetween>
                        ))}

                        {jumpListFiltered.map((staff, idx) => (
                            <FlexBetween key={idx} mt={0.5}>
                                <FlexBetween gap={0.5}>
                                    <Typography width="18px" align='right' color={primaryDark}>
                                        {crewList.length + idx + 1}.
                                    </Typography>
                                    <Typography flex={1} color={primaryDark}>
                                        {staff.staffName || 'N/A'}
                                    </Typography>
                                </FlexBetween>
                                <FlexBetween gap={0.5}>
                                    <Typography width="32px" textAlign="center">JS</Typography>
                                    {staff.contactNo ? (
                                        <Typography
                                            width="20px"
                                            aria-label={`Call ${staff.contactNo}`}
                                            title={`Call ${staff.contactNo}`}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            textAlign="center"
                                            component="a"
                                            href={`tel:${staff.contactNo.replace(/\D/g, '').trim()}`}
                                            sx={{ textDecoration: 'none', cursor: 'pointer', color: 'yellow.dark' }}
                                        >
                                            <Phone />
                                        </Typography>
                                    ) : (
                                        <Typography
                                            width="16px"
                                            aria-label="No contact number"
                                            title="No contact number"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            textAlign="center"
                                            sx={{ textDecoration: 'none', cursor: 'not-allowed', color: 'grey' }}
                                        >
                                            <PhoneDisabled />
                                        </Typography>
                                    )}
                                </FlexBetween>
                            </FlexBetween>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {!isLocked && crewList.length > 0 && (
                    <React.Fragment>
                        {reportType === 'apis' && (
                            <FormControl>
                                <InputLabel>Report</InputLabel>
                                <Select
                                    size="small"
                                    label="Type"
                                    value={reportName}
                                    onChange={(e) => setReportName(e.target.value)}
                                    sx={{ width: isMobile ? '6rem' : '8rem' }}
                                >
                                    <MenuItem value="all">General</MenuItem>
                                    <MenuItem value="z">China</MenuItem>
                                    <MenuItem value="vm">Macau</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <FormControl>
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={reportType}
                                size="small"
                                onChange={(e) => setReportType(e.target.value)}
                                sx={{ width: isMobile ? '6rem' : '8rem' }}
                            >
                                <MenuItem value="apis">APIS</MenuItem>
                                <MenuItem value="flightInfo">Flight Info</MenuItem>
                                <MenuItem value="crewList">Crew List</MenuItem>
                            </Select>
                        </FormControl>

                        <Button variant="outlined" onClick={generateExport}>
                            Export
                        </Button>
                    </React.Fragment>
                )}

                <Button variant="outlined" color="secondary" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>

            <DelayPopover
                anchorEl={popoverInfo.anchorEl}
                onClose={handlePopoverClose}
                description={popoverInfo.description}
            />

            {crewMemberDialog.open &&
                <CrewMemberDialog
                    open
                    onClose={handleCloseCrewMemberDialog}
                    crewMember={crewMemberDialog.member}
                />
            }

            {subDialog.type === 'roster' &&
                <RosterDetailsDialog
                    open
                    onClose={handleCloseSubDialog}
                    crewCode={subDialog.crewCode}
                    crewName={subDialog.crewName}
                    crewRosters={subDialog.rosters}
                />
            }
            {subDialog.type === 'leg' &&
                <LegMembersDialog
                    open
                    onClose={handleCloseSubDialog}
                    crewCode={subDialog.crewCode}
                    crewName={subDialog.crewName}
                    crewLegs={subDialog.rosters}
                />
            }
        </Dialog>
    );
};

export default CrewListDialog;
